import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';

interface Props {
  onGenerate: () => void;
  isGenerating?: boolean;
}

/**
 * What a phone shows when the week on screen has no meals.
 *
 * There was nothing here. `thisWeek.heroTitle` / `heroSubtitle` /
 * `generateMyWeek` have existed and been translated all along, wired only into
 * the two desktop layouts — on a phone an empty week rendered as bare day
 * headers with no way to fill them. What filled them instead was an effect that
 * generated a plan 800ms after you landed on any empty week and uploaded it,
 * which is why browsing forward quietly created weeks nobody asked for.
 *
 * Deleting that effect is only safe once this exists, so the two ship together.
 */
export function WeekEmptyState({ onGenerate, isGenerating }: Props) {
  const { t } = useTranslation();

  return (
    <View style={styles.wrap} testID="week-empty-state">
      <Text style={styles.title}>{t('thisWeek.heroTitle')}</Text>
      <Text style={styles.sub}>{t('thisWeek.heroSubtitle')}</Text>
      <Pressable
        style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed, isGenerating && styles.ctaBusy]}
        onPress={() => onGenerate()}
        disabled={isGenerating}
        accessibilityRole="button"
        accessibilityState={{ disabled: !!isGenerating, busy: !!isGenerating }}
        accessibilityLabel={t('thisWeek.generateMyWeek')}
        testID="week-generate"
      >
        {isGenerating ? (
          <ActivityIndicator size="small" color="#ffffff" />
        ) : (
          <Feather name="zap" size={15} color="#ffffff" />
        )}
        <Text style={styles.ctaText}>
          {isGenerating ? t('thisWeek.creatingYourPlan') : t('thisWeek.generateMyWeek')}
        </Text>
      </Pressable>
      <Text style={styles.hint}>{t('thisWeek.orAddManually')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 4,
    paddingHorizontal: 18,
    paddingVertical: 22,
    borderRadius: 16,
    alignItems: 'center',
    gap: 10,
    backgroundColor: Paprika.surfaceLow,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 21,
    lineHeight: 27,
    textAlign: 'center',
    color: Paprika.textPrimary,
  },
  sub: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
    color: Paprika.textSecondary,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 4,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: Paprika.primary,
  },
  ctaPressed: {
    opacity: 0.85,
  },
  ctaBusy: {
    opacity: 0.7,
  },
  ctaText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: '#ffffff',
  },
  hint: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
});
